import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { AlertTriangle, CheckCircle, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';

const SuspiciousActivityPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    const { data } = await supabase.from('suspicious_events').select('*').order('created_at', { ascending: false }).limit(200);
    setEvents(data || []);
    setLoading(false);
  };

  const markResolved = async (id) => {
    await supabase.from('suspicious_events').update({ resolved: true }).eq('id', id);
    setEvents(events.map(e => e.id === id ? { ...e, resolved: true } : e));
    toast.success('Marked as resolved');
  };

  const severityColor = { low: 'text-muted-foreground', medium: 'text-[hsl(var(--warning))]', high: 'text-orange-500', critical: 'text-destructive' };
  const severityBg = { low: 'bg-muted', medium: 'bg-[hsl(var(--warning))]/10', high: 'bg-orange-500/10', critical: 'bg-destructive/10' };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="suspicious-title">
            Suspicious Activity Detection
          </h1>
          <p className="text-base text-muted-foreground mt-1">
            {events.filter(e => !e.resolved).length} unresolved alerts
          </p>
        </div>

        {loading ? (
          <p className="text-muted-foreground font-mono text-center py-12">Loading...</p>
        ) : events.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border rounded-lg">
            <Shield className="w-16 h-16 text-[hsl(var(--success))] mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">All Clear</h3>
            <p className="text-sm text-muted-foreground">No suspicious activity detected</p>
          </div>
        ) : (
          <div className="space-y-3">
            {events.map(event => (
              <div key={event.id} className={`bg-card border border-border rounded-lg p-4 flex items-start justify-between gap-4 ${event.resolved ? 'opacity-50' : ''}`} data-testid="suspicious-event">
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`w-5 h-5 mt-0.5 ${severityColor[event.severity]}`} />
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded font-semibold uppercase ${severityBg[event.severity]} ${severityColor[event.severity]}`}>
                        {event.severity}
                      </span>
                      <span className="text-xs text-muted-foreground font-mono">{new Date(event.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm font-medium">{event.event_type}</p>
                    <p className="text-xs text-muted-foreground">{event.description}</p>
                  </div>
                </div>
                {!event.resolved && (
                  <Button size="sm" variant="outline" onClick={() => markResolved(event.id)} data-testid="resolve-event-btn">
                    <CheckCircle className="w-4 h-4 mr-1" /> Resolve
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SuspiciousActivityPage;
